import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
    Bold,
    Italic,
    Underline,
    List,
    ListOrdered,
    Link,
    AlignLeft,
    AlignCenter,
    AlignRight,
} from 'lucide-react';

const RichTextEditor = forwardRef(({ value, onChange }, ref) => {
    const editorRef = useRef(null);
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');

    useEffect(() => {
        if (editorRef.current && value !== editorRef.current.innerHTML) {
            editorRef.current.innerHTML = value;
        }
    }, [value]);

    const handleInput = () => {
        if (editorRef.current) {
            onChange(editorRef.current.innerHTML);
        }
    };

    const execCommand = (command, value = null) => {
        document.execCommand(command, false, value);
        editorRef.current?.focus();
        handleInput();
    };

    useImperativeHandle(ref, () => ({
        insertText: (text) => {
            editorRef.current?.focus();
            execCommand('insertText', text);
        }
    }));

    const handleAddLink = () => {
        if (linkUrl) {
            execCommand('createLink', linkUrl);
            setLinkUrl('');
            setShowLinkInput(false);
        }
    };

    const ToolbarButton = ({ icon: Icon, command, value, title }) => (
        <button
            type="button"
            onClick={() => execCommand(command, value)}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            title={title}
        >
            <Icon size={18} className="text-foreground" />
        </button>
    );

    return (
        <div className="border border-border rounded-xl overflow-hidden bg-background">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-1 p-2 bg-muted/30 border-b border-border">
                <ToolbarButton icon={Bold} command="bold" title="Bold" />
                <ToolbarButton icon={Italic} command="italic" title="Italic" />
                <ToolbarButton icon={Underline} command="underline" title="Underline" />

                <div className="w-px h-6 bg-border mx-1"></div>

                <ToolbarButton icon={AlignLeft} command="justifyLeft" title="Align Left" />
                <ToolbarButton icon={AlignCenter} command="justifyCenter" title="Align Center" />
                <ToolbarButton icon={AlignRight} command="justifyRight" title="Align Right" />

                <div className="w-px h-6 bg-border mx-1"></div>

                <ToolbarButton icon={List} command="insertUnorderedList" title="Bullet List" />
                <ToolbarButton icon={ListOrdered} command="insertOrderedList" title="Numbered List" />

                <div className="w-px h-6 bg-gray-300 mx-1"></div>

                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setShowLinkInput(!showLinkInput)}
                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                        title="Insert Link"
                    >
                        <Link size={18} className="text-foreground" />
                    </button>

                    {showLinkInput && (
                        <div className="absolute top-full left-0 mt-2 bg-card border border-border rounded-lg shadow-lg p-3 z-10 w-64">
                            <input
                                type="url"
                                value={linkUrl}
                                onChange={(e) => setLinkUrl(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleAddLink()}
                                placeholder="Enter URL"
                                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none text-sm bg-background text-foreground"
                            />
                            <div className="flex gap-2 mt-2">
                                <button
                                    type="button"
                                    onClick={handleAddLink}
                                    className="flex-1 px-3 py-1 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                                >
                                    Insert
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowLinkInput(false);
                                        setLinkUrl('');
                                    }}
                                    className="flex-1 px-3 py-1 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-muted/80 transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="w-px h-6 bg-gray-300 mx-1"></div>

                <select
                    onChange={(e) => execCommand('formatBlock', e.target.value)}
                    className="px-3 py-1 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-background text-foreground"
                    defaultValue="p"
                >
                    <option value="p">Paragraph</option>
                    <option value="h1">Heading 1</option>
                    <option value="h2">Heading 2</option>
                    <option value="h3">Heading 3</option>
                    <option value="h4">Heading 4</option>
                </select>
            </div>

            {/* Editor */}
            <div
                ref={editorRef}
                contentEditable
                onInput={handleInput}
                className="p-4 min-h-[200px] max-h-[400px] overflow-y-auto focus:outline-none prose prose-sm max-w-none"
                style={{
                    lineHeight: '1.6',
                }}
            />
        </div>
    );
});

export default RichTextEditor;
